import { ApolloError } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Alert, Stack, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GaiaColorPickerField } from "~/modules/app/components/GaiaColorPicker";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import { SpeciesFormFragment } from "./fragments.generated";

type Props = {
    initialData?: SpeciesFormFragment;
    onSubmit: (value: SpeciesFormInputs) => void;
    error?: ApolloError;
};

export default function SpeciesForm({ initialData, onSubmit, error }: Props) {
    const { t } = useTranslation("species");

    const {
        handleSubmit,
        register,
        setError,
        control,
        formState: { errors, isDirty, isSubmitted, isSubmitting },
    } = useForm<SpeciesFormInputs>({
        defaultValues: {
            name: initialData?.name ?? "",
            scientificName: initialData?.scientificName ?? "",
            description: initialData?.description ?? "",
            isRareSpecies: initialData?.isRareSpecies ?? false,
            mapColor: initialData?.mapColor ?? "#000000",
        },
    });

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    {...register("name")}
                    label={t("speciesForm.labelName")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />

                <TextField
                    {...register("scientificName")}
                    label={t("speciesForm.labelScientificName")}
                    error={!!errors.scientificName}
                    helperText={errors.scientificName?.message}
                />

                <TextField
                    {...register("description")}
                    label={t("speciesForm.labelDescription")}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    minRows={5}
                    multiline
                />
                <SwitchElement name="isRareSpecies" label={t("speciesForm.labelIsRareSpecies")} control={control} />

                <GaiaColorPickerField control={control} name="mapColor" label={t("speciesForm.labelMapColor")} />

                <GaiaHookFormSubmitButton control={control} startIcon={<Save />}>
                    {t("speciesForm.btnSubmit")}
                </GaiaHookFormSubmitButton>
            </Stack>
        </form>
    );
}

export type SpeciesFormInputs = {
    name: string;
    scientificName: string;
    description?: string | undefined | null;
    isRareSpecies: boolean;
    mapColor: string;
};
